import { Pencil, Trash2, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FleetStatusBadge, CONTRACT_STYLE } from "@/components/fleet/FleetBadges";
import { useSort, SortTh } from "@/lib/sort";

const Actions = ({ v, canEdit, isAdmin, onEdit, onDelete, onDeactivate }) => (
  <div className="flex justify-end gap-1">
    {canEdit && <Button size="sm" variant="ghost" onClick={() => onEdit(v)} data-testid={`fleet-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>}
    {onDeactivate && <Button size="sm" variant="ghost" className="text-slate-500" title="Disattiva (sposta nello storico)" onClick={() => onDeactivate(v)} data-testid={`fleet-deactivate-${v.id}`}><Archive className="h-4 w-4" /></Button>}
    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => onDelete(v)} data-testid={`fleet-delete-${v.id}`}><Trash2 className="h-4 w-4" /></Button>}
  </div>
);

export const FleetTable = ({ rows: data, meta, showStation, canEdit, isAdmin, onEdit, onDelete, onDeactivate, testId = "fleet-table" }) => {
  const { sorted: rows, sort, toggle } = useSort(data, {
    contract_type: (v) => meta.contracts[v.contract_type], status: (v) => meta.statuses[v.status],
    reason: (v) => (v.status !== "DISPONIBILE" ? v.reason : ""),
  });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  return (
  <>
    <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
      <div className="max-h-[calc(100vh-8rem)] overflow-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead className="sticky top-0 z-10 border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
            <tr>
              <S k="plate">Targa</S><S k="model">Modello</S>{showStation && <S k="station_name">Stazione</S>}
              <S k="contract_type">Contratto</S><S k="provider">Società</S><S k="status">Stato</S>
              <S k="reason">Motivo</S><S k="notes">Descrizione</S><S k="workshop">Officina / luogo</S><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((v) => (
              <tr key={v.id} className="hover:bg-slate-50" data-testid={`fleet-row-${v.id}`}>
                <td className="px-3 py-2">
                  <span className="font-mono-num rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-semibold">{v.plate}</span>
                  {v.last_transfer && <span className="mt-1 block whitespace-nowrap text-[11px] text-slate-500" data-testid={`fleet-last-transfer-${v.id}`}>Da {v.last_transfer.from} · {new Date(v.last_transfer.date).toLocaleDateString("it-IT")}</span>}
                </td>
                <td className="px-3 py-2">{v.model || "—"}</td>
                {showStation && <td className="whitespace-nowrap px-3 py-2">{v.station_name}</td>}
                <td className="px-3 py-2"><span className={`whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold ${CONTRACT_STYLE[v.contract_type]}`}>{meta.contracts[v.contract_type]}</span></td>
                <td className="px-3 py-2 text-slate-600">{v.provider || "—"}</td>
                <td className="px-3 py-2">
                  <FleetStatusBadge status={v.status} label={meta.statuses[v.status]} testId={`fleet-status-${v.id}`} />
                  {v.status === "TRASFERITO" && <span className="mt-0.5 block text-xs text-orange-700">→ {v.transfer_label}{v.transfer_station_id && ` (${meta.statuses[v.dest_status || "DISPONIBILE"]})`}</span>}
                  {v.__dest && <span className="mt-0.5 block text-xs text-slate-500" data-testid={`fleet-incoming-from-${v.id}`}>Ricevuto da {v.station_name}</span>}
                </td>
                <td className="px-3 py-2 text-slate-700">{(v.status !== "DISPONIBILE" && v.reason) || "—"}</td>
                <td className="max-w-xs px-3 py-2 text-xs text-slate-500">{v.notes || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{v.workshop || "—"}</td>
                <td className="px-3 py-2"><Actions v={v} canEdit={canEdit} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} onDeactivate={onDeactivate} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500">Nessun mezzo.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
    <div className="space-y-3 md:hidden" data-testid={`${testId}-cards`}>
      {rows.map((v) => (
        <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-mono-num font-semibold">{v.plate} <span className="font-sans text-sm font-normal text-slate-500">{v.model}</span></p>
              <p className="mt-1 text-xs text-slate-500">{showStation && `${v.station_name} · `}{meta.contracts[v.contract_type]}{v.provider && ` · ${v.provider}`}</p>
            </div>
            <FleetStatusBadge status={v.status} label={meta.statuses[v.status]} />
          </div>
          {(v.reason || v.notes || v.workshop || v.transfer_label) && (
            <p className="mt-2 text-sm text-slate-600">{[v.status === "TRASFERITO" && `→ ${v.transfer_label}`, v.status !== "DISPONIBILE" && v.reason, v.notes, v.workshop].filter(Boolean).join(" · ")}</p>
          )}
          <div className="mt-2 border-t pt-2"><Actions v={v} canEdit={canEdit} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} onDeactivate={onDeactivate} /></div>
        </div>
      ))}
      {rows.length === 0 && <p className="rounded-xl border border-dashed p-6 text-center text-sm text-slate-500">Nessun mezzo.</p>}
    </div>
  </>
  );
};
